import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { agentApi, uploadApi } from '../services/api';

interface AddAgentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editingAgent?: {
    id: number;
    name: string;
    url: string;
    industryTags: string[];
    description?: string;
    images?: string[];
    isPublic?: boolean;
    price?: number;
  } | null;
}

// 预设的行业标签
const INDUSTRY_TAG_OPTIONS = [
  '编程开发', '设计创意', '写作文案', '数据分析', 
  '营销推广', '教育培训', '金融财务', '医疗健康',
  '法律咨询', '人力资源', '电商运营', '客服支持',
  '翻译语言', '产品经理', '项目管理', '其他'
];

const AddAgentModal: React.FC<AddAgentModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  editingAgent
}) => {
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [industryTags, setIndustryTags] = useState<string[]>([]);
  const [description, setDescription] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [existingImages, setExistingImages] = useState<string[]>([]);
  const [isPublic, setIsPublic] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isParsing, setIsParsing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [agentPrice, setAgentPrice] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const isEditing = !!editingAgent;

  // 初始化编辑数据
  useEffect(() => {
    if (editingAgent) {
      setName(editingAgent.name);
      setUrl(editingAgent.url);
      setIndustryTags(editingAgent.industryTags || []);
      setDescription(editingAgent.description || '');
      setExistingImages(editingAgent.images || []);
      setIsPublic(editingAgent.isPublic !== false);
      setAgentPrice(editingAgent.price || 0);
    } else {
      resetForm();
    }
  }, [editingAgent, isOpen]);

  const resetForm = () => {
    setName('');
    setUrl('');
    setIndustryTags([]);
    setDescription('');
    setSelectedFiles([]);
    setExistingImages([]);
    setIsPublic(true);
    setAgentPrice(0);
  };

  // 解析TRAE分享文案
  const handleParseUrl = async () => {
    if (!url.trim()) return;
    
    setIsParsing(true);
    try {
      const result = await agentApi.parseShareText(url);
      if (result.success && result.data) {
        if (result.data.url) {
          // 保持原始文案，但提示解析成功
          const versionText = result.data.version === 'international' ? '（国际版）' : 
                             result.data.version === 'china' ? '（国内版）' : '';
          toast.success(`链接解析成功${versionText}`);
        }
        if (result.data.name && !name) {
          setName(result.data.name);
          toast.success(`已自动填充名称: ${result.data.name}`);
        }
        if (!result.data.isValid) {
          toast.error('未检测到有效的TRAE链接（支持 s.trae.ai 和 s.trae.com.cn）');
        }
      }
    } catch (error) {
      console.error('解析失败:', error);
    } finally {
      setIsParsing(false);
    }
  };

  // 处理标签选择
  const toggleTag = (tag: string) => {
    if (industryTags.includes(tag)) {
      setIndustryTags(industryTags.filter(t => t !== tag));
    } else if (industryTags.length < 5) {
      setIndustryTags([...industryTags, tag]);
    } else {
      toast.error('最多选择5个行业标签');
    }
  };

  // 处理文件（用于选择和拖拽）
  const processFiles = (files: File[]) => {
    const totalImages = existingImages.length + selectedFiles.length + files.length;
    
    if (totalImages > 9) {
      toast.error('最多上传9张图片');
      return;
    }

    const validFiles = files.filter(file => {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} 不是有效的图片文件`);
        return false;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} 超过5MB限制`);
        return false;
      }
      return true;
    });

    setSelectedFiles(prev => [...prev, ...validFiles]);
  };

  // 处理图片选择
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    processFiles(files);
  };

  // 处理拖拽事件
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    processFiles(files);
  };

  // 移除已选图片
  const removeSelectedFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  // 移除已有图片
  const removeExistingImage = (index: number) => {
    setExistingImages(prev => prev.filter((_, i) => i !== index));
  };

  // 提交表单
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!name.trim()) {
      toast.error('请输入智能体名称');
      return;
    }
    if (!url.trim()) {
      toast.error('请输入智能体URL或分享文案');
      return;
    }
    
    // 验证价格
    if (agentPrice < 0 || agentPrice > 1000) {
      toast.error('价格必须在0-1000元之间');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // 上传新图片
      let uploadedUrls: string[] = [];
      if (selectedFiles.length > 0) {
        toast.loading('正在上传图片...', { id: 'uploading' });
        uploadedUrls = await uploadApi.uploadImages(selectedFiles, (current, total) => {
          toast.loading(`正在上传图片 ${current}/${total}...`, { id: 'uploading' });
        });
        toast.dismiss('uploading');
      }

      // 合并图片
      const allImages = [...existingImages, ...uploadedUrls].slice(0, 9);

      const agentData = {
        name: name.trim(),
        url: url.trim(),
        industryTags,
        description: description.trim(),
        images: allImages,
        isPublic,
        price: agentPrice
      };

      if (isEditing && editingAgent) {
        await agentApi.updateAgent(editingAgent.id, agentData);
        toast.success('智能体更新成功');
      } else {
        await agentApi.createAgent(agentData);
        toast.success('智能体创建成功');
      }

      onSuccess();
      onClose();
      resetForm();
    } catch (error: any) {
      toast.error(error.message || '操作失败');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* 头部 */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                <i className="fas fa-robot text-primary mr-2"></i>
                {isEditing ? '编辑智能体' : '添加智能体'}
              </h2>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <i className="fas fa-times text-lg"></i>
              </button>
            </div>

            {/* 表单 */}
            <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              {/* 智能体名称 */}
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  智能体名称 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="例如：APP UI设计师"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent transition-all"
                  maxLength={100}
                />
              </div>

              {/* 智能体URL */}
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  智能体URL或分享文案 <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <textarea
                    value={url}
                    onChange={e => setUrl(e.target.value)}
                    onBlur={handleParseUrl}
                    placeholder="粘贴TRAE分享链接，例如：&#10;国际版：https://s.trae.ai/a/76dab7?region=sg&#10;国内版：https://s.trae.com.cn/a/xxxxxx&#10;或粘贴完整分享文案"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent transition-all resize-none"
                    rows={3}
                  />
                  {isParsing && (
                    <div className="absolute right-3 top-3">
                      <i className="fas fa-spinner fa-spin text-primary"></i>
                    </div>
                  )}
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  支持直接粘贴URL或TRAE的完整分享文案，系统会自动解析（支持国际版 s.trae.ai 和国内版 s.trae.com.cn）
                </p>
              </div>

              {/* 行业标签 */}
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  行业标签 <span className="text-xs text-gray-500">(最多5个)</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {INDUSTRY_TAG_OPTIONS.map(tag => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => toggleTag(tag)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                        industryTags.includes(tag)
                          ? 'bg-primary text-white'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                      }`}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>

              {/* 智能体介绍 */}
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  智能体介绍
                </label>
                <textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="介绍这个智能体的功能和使用场景..."
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent transition-all resize-none"
                  rows={3}
                  maxLength={500}
                />
              </div>

              {/* 效果展示图片 */}
              <div className="mb-5">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  效果展示图片 <span className="text-xs text-gray-500">(最多9张，支持拖拽上传)</span>
                </label>
                
                {/* 拖拽上传区域 */}
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={`relative rounded-xl border-2 border-dashed p-4 transition-all ${
                    isDragging 
                      ? 'border-primary bg-primary/10 scale-[1.02]' 
                      : 'border-gray-300 dark:border-gray-600'
                  }`}
                >
                  {/* 拖拽提示遮罩 */}
                  {isDragging && (
                    <div className="absolute inset-0 flex items-center justify-center bg-primary/20 rounded-xl z-10">
                      <div className="text-center">
                        <i className="fas fa-cloud-upload-alt text-4xl text-primary mb-2"></i>
                        <p className="text-primary font-medium">释放以上传图片</p>
                      </div>
                    </div>
                  )}
                  
                  {/* 已有图片 */}
                  {existingImages.length > 0 && (
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      {existingImages.map((img, index) => (
                        <div key={`existing-${index}`} className="relative aspect-square rounded-lg overflow-hidden group">
                          <img src={img} alt="" className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => removeExistingImage(index)}
                            className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                          >
                            <i className="fas fa-times text-xs"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* 新选择的图片预览 */}
                  {selectedFiles.length > 0 && (
                    <div className="grid grid-cols-3 gap-2 mb-3">
                      {selectedFiles.map((file, index) => (
                        <div key={`new-${index}`} className="relative aspect-square rounded-lg overflow-hidden group border-2 border-dashed border-primary">
                          <img src={URL.createObjectURL(file)} alt="" className="w-full h-full object-cover" />
                          <button
                            type="button"
                            onClick={() => removeSelectedFile(index)}
                            className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                          >
                            <i className="fas fa-times text-xs"></i>
                          </button>
                          <span className="absolute bottom-1 left-1 text-xs bg-primary text-white px-1 rounded">新</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* 上传按钮 */}
                  {existingImages.length + selectedFiles.length < 9 && (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full py-4 rounded-lg text-gray-500 dark:text-gray-400 hover:text-primary transition-colors flex flex-col items-center justify-center"
                    >
                      <i className="fas fa-cloud-upload-alt text-3xl mb-2"></i>
                      <span>点击选择或拖拽图片到此处</span>
                    </button>
                  )}
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </div>
              </div>

              {/* 公开设置 */}
              <div className="mb-4">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isPublic}
                    onChange={e => setIsPublic(e.target.checked)}
                    className="w-5 h-5 text-primary rounded border-gray-300 focus:ring-primary"
                  />
                  <span className="ml-3 text-sm text-gray-700 dark:text-gray-300">
                    公开到智能体市场
                  </span>
                </label>
              </div>

              {/* 价格设置 */}
              <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-xl">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                      <i className="fas fa-tag text-emerald-500 mr-2"></i>
                      设置价格
                    </h4>
                    <p className="text-xs text-gray-600 dark:text-gray-300">设置价格后，用户需付费才能使用智能体（0表示免费）</p>
                  </div>
                  <div className="flex items-center ml-4">
                    <span className="text-gray-500 mr-2">¥</span>
                    <input
                      type="number"
                      value={agentPrice}
                      onChange={e => setAgentPrice(parseFloat(e.target.value) || 0)}
                      min="0"
                      max="1000"
                      step="0.01"
                      className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-right"
                      placeholder="0.00"
                    />
                  </div>
                </div>
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-2">
                  <i className="fas fa-info-circle mr-1"></i>
                  价格范围：0-1000元，设置后其他用户需支付才能应用此智能体
                </p>
              </div>

              {/* 提交按钮 */}
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 py-3 px-4 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  取消
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 py-3 px-4 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      提交中...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check mr-2"></i>
                      {isEditing ? '保存修改' : '添加智能体'}
                    </>
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AddAgentModal;

